import React, {useState, useEffect, useMemo} from "react";
import { useParams, Link } from 'react-router-dom'
import axios from "axios";

import TopNav from "../components/TopNav";
import "./TourPage.css";

// const TOURS_CACHE_KEY = "tpAv_tours_cache_v1";

// Helper to format date
const formatDate = (epoch) => {
    if (!epoch || epoch === 0) return "Not yet flown";
    // Assuming epoch is seconds (PHP standard)
    return new Date(epoch * 1000).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

// Helper to calculate days since last leg
const getDaysSinceLastLeg = (legs) => {
    if (!legs || !Array.isArray(legs)) return null;
    const flown = legs.filter(l => l.epoch > 0);
    if (flown.length === 0) return null;
    
    const lastEpoch = Math.max(...flown.map(l => l.epoch));
    const now = Date.now() / 1000;
    const diff = now - lastEpoch;
    return Math.floor(diff / (3600 * 24));
};

// Generate a colorful procedural SVG placeholder based on tour ID
const generatePlaceholderSvg = (id) => {
    // Create a deterministic hash from the ID
    const hash = String(id).split('').reduce((acc, char) => {
        return ((acc << 5) - acc) + char.charCodeAt(0) | 0;
    }, 0);
    
    // Generate hue values based on hash
    const hue1 = Math.abs(hash % 360);
    const hue2 = Math.abs((hash >> 8) % 360);
    const hue3 = Math.abs((hash >> 16) % 360);
    
    // Create gradient colors
    const color1 = `hsl(${hue1}, 70%, 55%)`;
    const color2 = `hsl(${hue2}, 65%, 45%)`;
    const color3 = `hsl(${hue3}, 75%, 60%)`;
    
    // Generate geometric pattern positions based on hash
    const cx1 = 20 + Math.abs((hash >> 2) % 60);
    const cy1 = 20 + Math.abs((hash >> 4) % 60);
    const cx2 = 40 + Math.abs((hash >> 6) % 40);
    const cy2 = 30 + Math.abs((hash >> 8) % 50);
    const cx3 = 60 + Math.abs((hash >> 10) % 30);
    const cy3 = 40 + Math.abs((hash >> 12) % 40);
    
    const r1 = 30 + Math.abs((hash >> 14) % 40);
    const r2 = 25 + Math.abs((hash >> 16) % 35);
    const r3 = 20 + Math.abs((hash >> 18) % 30);
    
    // Create SVG
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 150">
        <defs>
            <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:${color1};stop-opacity:1" />
                <stop offset="50%" style="stop-color:${color2};stop-opacity:1" />
                <stop offset="100%" style="stop-color:${color3};stop-opacity:1" />
            </linearGradient>
            <filter id="blur">
                <feGaussianBlur stdDeviation="3" />
            </filter>
        </defs>
        <rect width="200" height="150" fill="url(#bg)" />
        <circle cx="${cx1}" cy="${cy1}" r="${r1}" fill="rgba(255,255,255,0.2)" filter="url(#blur)" />
        <circle cx="${cx2}" cy="${cy2}" r="${r2}" fill="rgba(255,255,255,0.15)" filter="url(#blur)" />
        <circle cx="${cx3}" cy="${cy3}" r="${r3}" fill="rgba(255,255,255,0.1)" filter="url(#blur)" />
        <circle cx="${200 - cx1}" cy="${150 - cy1}" r="${r1 * 0.6}" fill="rgba(0,0,0,0.1)" filter="url(#blur)" />
        <circle cx="${200 - cx2}" cy="${150 - cy2}" r="${r2 * 0.5}" fill="rgba(0,0,0,0.08)" filter="url(#blur)" />
    </svg>`;
    
    return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// Memoized Image Component
const TourImage = React.memo(({ id, alt, darkMode }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    return (
        <div className={`tp-card ${darkMode ? 'tp-dark-mode' : ''}`}>
            <div className="tp-image-container">
                {loading && !error && (
                    <div className="tp-spinner-overlay">
                        <div className="tp-spinner"></div>
                    </div>
                )}
                <img
                    className={`tp-image ${loading ? 'tp-hidden' : ''}`}
                    src={`${process.env.PUBLIC_URL}/tours/tour_${id}.jpg`}
                    alt=""
                    loading="lazy"
                    onLoad={() => setLoading(false)}
                    onError={() => { setLoading(false); setError(true); }}
                />
                {error && (
                    <div className="tp-placeholder-container">
                        <img
                            className="tp-image"
                            src={generatePlaceholderSvg(id)}
                            alt=""
                        />
                        {alt && (
                            <div className="tp-placeholder-text">{alt}</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});

// Table Component with Pagination Only
const TourLegsTable = ({ legs, darkMode }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Pagination
    const totalPages = Math.ceil((legs || []).length / itemsPerPage);
    const currentItems = (legs || []).slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className={`tp-card ${darkMode ? 'tp-dark-mode' : ''}`}>
            {/* <h2 className="tp-card-title">Tour Legs</h2> */}
            
            <div className="tp-table-container">
                <table className="tp-table">
                    <thead>
                        <tr>
                            <th>Leg</th>
                            <th>Origin</th>
                            <th>Destination</th>
                            <th>Date Completed</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length > 0 ? (
                            currentItems.map((leg, index) => (
                                <tr key={index} className={leg.epoch > 0 ? 'tp-row-flown' : 'tp-row-pending'}>
                                    <td>{leg.legnum}</td>
                                    <td>{leg.orig}</td>
                                    <td>{leg.dest}</td>
                                    <td>{formatDate(leg.epoch)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" style={{textAlign: 'center'}}>No legs found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="tp-pagination">
                    <button 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="tp-page-btn"
                    >
                        Previous
                    </button>
                    <span style={{alignSelf: 'center'}}>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button 
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="tp-page-btn"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

const TourPage = (props) => {
    const { id } = useParams()
    const [data, setData] = useState(false);
    const [legData, setLegData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
      getTour();
      return () => {}
    }, [id])


    const getTour = async (obj) => {
        // Get the tours data
        // try {
        //     const raw = localStorage.getItem(TOURS_CACHE_KEY);
        //     if (raw) {
        //         const parsed = JSON.parse(raw);
        //         const cachedTours = parsed?.tours;
        //         if (Array.isArray(cachedTours)) {
        //             setError(null);
        //             for(let i=0; i<cachedTours.length; i++){
        //                 const row = cachedTours[i];
        //                 if(row.id === id){
        //                     console.log(row)
        //                     setData(row);
        //                     break;
        //                 }
        //             }
        //         }
        //     }
        // } catch (e) {
        //     // Ignore cache parse/access errors and fall back to network.
        // }

        try {
            console.log("getTour()")
            setLoading(true);
            const response = await axios.get("/api/tours/get_selected", {
            params: { _t: Date.now(),  id: id }
            });                                
            console.log(response);            

            console.log(response.data)
            setData(response.data)
            setLegData(response.data.tourlegs); 
            
        }
        catch (e) {
            console.log(e)
            setError(e);
            setData(false); 
        }
        finally{
            setLoading(false); // Stop loading after any error
        }
    }    

    // Derived State
    const tourStatus = useMemo(() => {
        if (!legData || legData.length === 0) return 'unknown';
        const allFlown = legData.every(l => l.epoch > 0);
        const noneFlown = legData.every(l => l.epoch === 0);
        
        if (allFlown) return 'completed';
        if (noneFlown) return 'not-started';
        return 'in-progress';
    }, [legData]);

    const daysSinceLast = useMemo(() => getDaysSinceLastLeg(legData), [legData]);

    return(
        <React.Fragment>
            <TopNav page={props.page} darkMode={props.darkmode} />
            <div>{error}</div>
            <div className={`tp-container ${props.darkmode ? 'tp-dark-mode' : ''}`}>
                
                <Link to="/tours" className="tp-back-link">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
                    </svg>
                    Back to Tours
                </Link>

                {/* Header Section */}
                <header className="tp-header">
                    <h1 className="tp-title">
                        {data?.name && data.name}
                    </h1>
                    
                    {tourStatus === 'completed' && (
                        <span className="tp-status-badge tp-status-completed">Completed</span>
                    )}
                    {tourStatus === 'in-progress' && (
                        <span className="tp-status-badge tp-status-inprogress">In Progress</span>
                    )}
                    {tourStatus === 'not-started' && (
                        <span className="tp-status-badge tp-status-notstarted">Not Started</span>
                    )}
                </header>

                {/* Loading Overlay for initial fetch */}
                {loading && !legData.length && (
                    <div className="tp-spinner-overlay" style={{position: 'fixed'}}>
                        <div className="tp-spinner"></div>
                    </div>
                )}

                <div className="tp-grid">
                    {/* Left Column */}
                    <div className="tp-left-col">
                        <TourImage id={id} alt={data?.name} darkMode={props.darkmode} />
                        
                        <TourLegsTable legs={legData} darkMode={props.darkmode} />
                    </div>

                    {/* Right Column */}
                    <div className="tp-right-col">
                        
                        {/* Status Messages */}
                        {tourStatus === 'completed' && (
                            <div className={`tp-message-box tp-message-success ${props.darkmode ? 'tp-dark-mode' : ''}`}>
                                <h3>Congratulations!</h3>
                                <p>You have successfully completed this tour.</p>
                            </div>
                        )}

                        {tourStatus === 'in-progress' && daysSinceLast !== null && (
                            <div className={`tp-message-box tp-message-info ${props.darkmode ? 'tp-dark-mode' : ''}`}>
                                <h3>Keep it up!</h3>
                                <p>It has been {daysSinceLast} days since your last leg. We'd love to see you back in the skies soon to complete this tour!</p>
                            </div>
                        )}

                        {tourStatus === 'not-started' && (
                            <div className={`tp-message-box tp-message-info ${props.darkmode ? 'tp-dark-mode' : ''}`}>
                                <h3>Ready for takeoff?</h3>
                                <p>You haven't started this tour yet. Start your engines!</p>
                            </div>
                        )}

                        {/* Summary */}
                        <div className={`tp-card ${props.darkmode ? 'tp-dark-mode' : ''}`}>
                            {/* <h2 className="tp-card-title">Summary</h2> */}
                            <div className="tp-summary">
                                {data?.summary || "No summary available."}

                            </div>
                        </div>

                        {/* Description */}
                        <div className={`tp-card ${props.darkmode ? 'tp-dark-mode' : ''}`}>
                            <h2 className="tp-card-title">Description</h2>
                            <div className="tp-description">
                                {data?.descrip ? (
                                    data.descrip
                                ) : "No description available."}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    )

}
export default TourPage;
